import React, { useState } from 'react';
import { KeywordItem } from '../types';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';

interface KeywordTableProps {
  keywords: KeywordItem[];
}

export const KeywordTable: React.FC<KeywordTableProps> = ({ keywords = [] }) => {
  const [filter, setFilter] = useState<'all' | 'opportunities' | 'top'>('all');
  const safeKeywords = Array.isArray(keywords) ? keywords : [];

  const filtered = safeKeywords.filter((k) => {
    if (filter === 'opportunities') return k.isOpportunity || k.movement > 0;
    if (filter === 'top') return k.rank <= 3;
    return true;
  });

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">ASO & Discovery</span>
          <h3 className="text-lg font-bold text-slate-900 tracking-tight">Keyword Intelligence</h3>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-lg transition-all ${
              filter === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            All ({safeKeywords.length})
          </button>
          <button
            onClick={() => setFilter('opportunities')}
            className={`px-3 py-1 rounded-lg transition-all ${
              filter === 'opportunities' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Opportunities
          </button>
          <button
            onClick={() => setFilter('top')}
            className={`px-3 py-1 rounded-lg transition-all ${
              filter === 'top' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Top 3
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-100 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              <th className="pb-3 pl-2">Keyword</th>
              <th className="pb-3 text-center">Rank</th>
              <th className="pb-3 text-center">Search Vol</th>
              <th className="pb-3 text-center">Difficulty</th>
              <th className="pb-3 text-right pr-2">Movement</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-slate-500 font-medium">
                  No keyword ranking data available
                </td>
              </tr>
            ) : (
              filtered.map((item) => {
              const isUp = item.movement > 0;
              const isDown = item.movement < 0;

              return (
                <tr key={item.keyword} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3.5 pl-2 font-semibold text-slate-900 flex items-center gap-2">
                    <span>{item.keyword}</span>
                    {item.isOpportunity && (
                      <span className="text-[10px] bg-sky-100 text-sky-800 font-bold px-1.5 py-0.5 rounded">
                        Opportunity
                      </span>
                    )}
                  </td>
                  <td className="py-3.5 text-center">
                    <span className="inline-flex items-center justify-center font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md min-w-[32px]">
                      #{item.rank}
                    </span>
                  </td>
                  <td className="py-3.5 text-center">
                    <div className="inline-flex items-center gap-1.5 font-medium text-slate-700">
                      <span>{item.searchVolume}</span>
                      <div className="w-10 bg-slate-100 h-1.5 rounded-full overflow-hidden hidden sm:block">
                        <div
                          className="bg-blue-500 h-full rounded-full"
                          style={{ width: `${item.searchVolume}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="py-3.5 text-center">
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                        item.difficulty === 'High'
                          ? 'bg-rose-50 text-rose-700 border border-rose-100'
                          : item.difficulty === 'Medium'
                          ? 'bg-amber-50 text-amber-700 border border-amber-100'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                      }`}
                    >
                      {item.difficulty}
                    </span>
                  </td>
                  <td className="py-3.5 text-right pr-2 font-bold">
                    {isUp && (
                      <span className="inline-flex items-center gap-0.5 text-emerald-600">
                        <ArrowUp className="w-3 h-3" strokeWidth={2.5} />
                        <span>{item.movement}</span>
                      </span>
                    )}
                    {isDown && (
                      <span className="inline-flex items-center gap-0.5 text-rose-500">
                        <ArrowDown className="w-3 h-3" strokeWidth={2.5} />
                        <span>{Math.abs(item.movement)}</span>
                      </span>
                    )}
                    {item.movement === 0 && (
                      <span className="inline-flex items-center gap-0.5 text-slate-400 font-normal">
                        <Minus className="w-3 h-3" strokeWidth={2} />
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
